<mat-progress-bar [mode]="loading() ? 'indeterminate' : 'determinate'" [value]="100" class="page-progress" aria-label="Loading"></mat-progress-bar>

<lib-page-header [title]="(capability()?.code || code) + ' ' + (capability()?.version || version)"
                 screenName="Capability"
                 backTo="/administration/capabilities">
    <mat-icon icon>extension</mat-icon>
    <ng-container subtitle *ngIf="capability()">
        <span [class]="statusClass(capability()?.status)">{{ capability()?.status }}</span>
        <span class="cap-created-by">by {{ capability()?.createdBy }}</span>
    </ng-container>
    <ng-container actions>
        <button mat-stroked-button *ngIf="canManage() && isInProgress()" (click)="promote()">
            <mat-icon>publish</mat-icon> Promote
        </button>
        <button mat-stroked-button *ngIf="canManage() && isActive()" (click)="deactivate()">
            <mat-icon>pause_circle</mat-icon> Deactivate
        </button>
        <button mat-stroked-button *ngIf="canManage() && isInactive()" (click)="decommission()">
            <mat-icon>block</mat-icon> Decommission
        </button>
        <button mat-icon-button *ngIf="canManage() && isInProgress()"
                (click)="deleteDraft()" matTooltip="Delete draft" aria-label="Delete draft">
            <mat-icon>delete_outline</mat-icon>
        </button>
        <button mat-icon-button (click)="refresh()" matTooltip="Refresh" aria-label="Refresh">
            <mat-icon>refresh</mat-icon>
        </button>
    </ng-container>
</lib-page-header>

<section class="main-content" *ngIf="capability() as c">

    <mat-card class="app-panel meta-card">
        <mat-card-content>
            <div class="meta-row"><span class="meta-label">Description</span>
                <span class="meta-value" *ngIf="c.description">{{ c.description }}</span>
                <span class="meta-value muted" *ngIf="!c.description">—</span>
            </div>
            <div class="meta-row"><span class="meta-label">Created</span>
                <span class="meta-value">{{ c.createdAt | date:'medium' }}</span>
            </div>
            <div class="meta-row"><span class="meta-label">Last modified</span>
                <span class="meta-value">{{ c.lastModifiedAt | date:'medium' }}</span>
            </div>
        </mat-card-content>
    </mat-card>

    <!-- INPUT fields -->
    <mat-card class="app-panel fields-card">
        <mat-card-header>
            <mat-card-title>
                <mat-icon class="card-title-icon">input</mat-icon>
                INPUT fields
                <span class="field-count">{{ inputFields().length }}</span>
            </mat-card-title>
            <span class="spacer"></span>
            <button mat-stroked-button *ngIf="canManage() && isInProgress()"
                    (click)="addField()">
                <mat-icon>add</mat-icon> Add INPUT field
            </button>
        </mat-card-header>
        <mat-card-content>
            <table mat-table [dataSource]="inputFields()" class="data-table"
                   *ngIf="inputFields().length > 0">
                <ng-container matColumnDef="name">
                    <th mat-header-cell *matHeaderCellDef>Name</th>
                    <td mat-cell *matCellDef="let f"><span class="mono">{{ f.fieldName }}</span></td>
                </ng-container>
                <ng-container matColumnDef="group">
                    <th mat-header-cell *matHeaderCellDef>Group</th>
                    <td mat-cell *matCellDef="let f">{{ f.groupName || '—' }}</td>
                </ng-container>
                <ng-container matColumnDef="kind">
                    <th mat-header-cell *matHeaderCellDef>Kind</th>
                    <td mat-cell *matCellDef="let f">{{ f.fieldKind }}</td>
                </ng-container>
                <ng-container matColumnDef="type">
                    <th mat-header-cell *matHeaderCellDef>Type</th>
                    <td mat-cell *matCellDef="let f">
                        <span *ngIf="f.fieldKind === 'Connector'" class="mono">{{ f.connectorType }}</span>
                        <span *ngIf="f.fieldKind === 'Data'" class="mono">{{ f.dataType }}</span>
                        <span *ngIf="f.userdataType" class="userdata-type-suffix">({{ f.userdataType }})</span>
                    </td>
                </ng-container>
                <ng-container matColumnDef="cardinality">
                    <th mat-header-cell *matHeaderCellDef>Cardinality</th>
                    <td mat-cell *matCellDef="let f">{{ f.cardinality }}</td>
                </ng-container>
                <ng-container matColumnDef="required">
                    <th mat-header-cell *matHeaderCellDef>Required</th>
                    <td mat-cell *matCellDef="let f">
                        <mat-icon class="check-icon" *ngIf="f.required">check</mat-icon>
                        <span *ngIf="!f.required" class="muted">—</span>
                    </td>
                </ng-container>
                <ng-container matColumnDef="description">
                    <th mat-header-cell *matHeaderCellDef>Description</th>
                    <td mat-cell *matCellDef="let f">
                        <span *ngIf="f.description">{{ f.description }}</span>
                        <span *ngIf="!f.description" class="muted">—</span>
                    </td>
                </ng-container>
                <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef class="col-actions">Actions</th>
                    <td mat-cell *matCellDef="let f" class="col-actions">
                        <button mat-icon-button matTooltip="Edit field" aria-label="Edit field"
                                *ngIf="canManage() && isInProgress()"
                                (click)="editField(f)">
                            <mat-icon>edit</mat-icon>
                        </button>
                        <button mat-icon-button matTooltip="Delete field" aria-label="Delete field"
                                *ngIf="canManage() && isInProgress()"
                                (click)="deleteField(f)">
                            <mat-icon>delete_outline</mat-icon>
                        </button>
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="fieldColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: fieldColumns"></tr>
            </table>
            <div class="empty-state-inline" *ngIf="inputFields().length === 0">
                <mat-icon>info</mat-icon> No INPUT fields yet.
                <span *ngIf="canManage() && isInProgress()">Use "Add INPUT field" above.</span>
            </div>
        </mat-card-content>
    </mat-card>

    <!-- OUTPUT fields -->
    <mat-card class="app-panel fields-card">
        <mat-card-header>
            <mat-card-title>
                <mat-icon class="card-title-icon">output</mat-icon>
                OUTPUT fields
                <span class="field-count">{{ outputFields().length }}</span>
            </mat-card-title>
            <span class="spacer"></span>
            <button mat-stroked-button *ngIf="canManage() && isInProgress()"
                    (click)="addField()">
                <mat-icon>add</mat-icon> Add OUTPUT field
            </button>
        </mat-card-header>
        <mat-card-content>
            <table mat-table [dataSource]="outputFields()" class="data-table"
                   *ngIf="outputFields().length > 0">
                <ng-container matColumnDef="name">
                    <th mat-header-cell *matHeaderCellDef>Name</th>
                    <td mat-cell *matCellDef="let f"><span class="mono">{{ f.fieldName }}</span></td>
                </ng-container>
                <ng-container matColumnDef="group">
                    <th mat-header-cell *matHeaderCellDef>Group</th>
                    <td mat-cell *matCellDef="let f">{{ f.groupName || '—' }}</td>
                </ng-container>
                <ng-container matColumnDef="kind">
                    <th mat-header-cell *matHeaderCellDef>Kind</th>
                    <td mat-cell *matCellDef="let f">{{ f.fieldKind }}</td>
                </ng-container>
                <ng-container matColumnDef="type">
                    <th mat-header-cell *matHeaderCellDef>Type</th>
                    <td mat-cell *matCellDef="let f">
                        <span class="mono">{{ f.dataType }}</span>
                        <span *ngIf="f.userdataType" class="userdata-type-suffix">({{ f.userdataType }})</span>
                    </td>
                </ng-container>
                <ng-container matColumnDef="cardinality">
                    <th mat-header-cell *matHeaderCellDef>Cardinality</th>
                    <td mat-cell *matCellDef="let f">{{ f.cardinality }}</td>
                </ng-container>
                <ng-container matColumnDef="required">
                    <th mat-header-cell *matHeaderCellDef>Required</th>
                    <td mat-cell *matCellDef="let f">
                        <mat-icon class="check-icon" *ngIf="f.required">check</mat-icon>
                        <span *ngIf="!f.required" class="muted">—</span>
                    </td>
                </ng-container>
                <ng-container matColumnDef="description">
                    <th mat-header-cell *matHeaderCellDef>Description</th>
                    <td mat-cell *matCellDef="let f">
                        <span *ngIf="f.description">{{ f.description }}</span>
                        <span *ngIf="!f.description" class="muted">—</span>
                    </td>
                </ng-container>
                <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef class="col-actions">Actions</th>
                    <td mat-cell *matCellDef="let f" class="col-actions">
                        <button mat-icon-button matTooltip="Edit field" aria-label="Edit field"
                                *ngIf="canManage() && isInProgress()"
                                (click)="editField(f)">
                            <mat-icon>edit</mat-icon>
                        </button>
                        <button mat-icon-button matTooltip="Delete field" aria-label="Delete field"
                                *ngIf="canManage() && isInProgress()"
                                (click)="deleteField(f)">
                            <mat-icon>delete_outline</mat-icon>
                        </button>
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="fieldColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: fieldColumns"></tr>
            </table>
            <div class="empty-state-inline" *ngIf="outputFields().length === 0">
                <mat-icon>info</mat-icon> No OUTPUT fields yet.
                <span *ngIf="canManage() && isInProgress()">Use "Add OUTPUT field" above.</span>
            </div>
        </mat-card-content>
    </mat-card>
</section>
